import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { THEME_LIST, applyTheme } from "@/lib/themes";
import { toast } from "sonner";
import { Check } from "lucide-react";
import ImageUploader from "@/components/ImageUploader";

export default function TAdminBranding() {
  const [branding, setBranding] = useState({ app_name: "", logo_url: "", banner_url: "", theme_preset: "warm-coral", tagline: "" });

  // What the server holds. The logo is saved on its own, as soon as it is
  // uploaded, without also saving edits that are still being made.
  const saved = useRef(null);

  useEffect(() => {
    api.get("/tenant/me").then((r) => {
      const b = r.data.tenant?.branding || {};
      setBranding((prev) => { saved.current = { ...prev, ...b }; return saved.current; });
      applyTheme(b.theme_preset || "warm-coral");
    });
  }, []);

  const failed = (err, fallback) => {
    const detail = err?.response?.data?.detail;
    toast.error(typeof detail === "string" ? detail : fallback);
  };

  const save = async () => {
    try {
      await api.put("/tenant/branding", branding);
      saved.current = branding;
      toast.success("Branding updated — your PWA icon, manifest & theme are now live");
    } catch (err) { failed(err, "Could not save the branding"); }
  };

  // An uploaded logo that is not saved never reaches the website or the
  // Android and iOS apps, so it is saved the moment it is uploaded or cleared.
  const saveLogo = async (url) => {
    const before = branding.logo_url;
    setBranding((b) => ({ ...b, logo_url: url }));
    if (!saved.current) return;
    try {
      const next = { ...saved.current, logo_url: url || null };
      await api.put("/tenant/branding", next);
      saved.current = next;
      toast.success(url ? "Logo saved — used by your website and your next app build" : "Logo removed");
    } catch (err) {
      setBranding((b) => ({ ...b, logo_url: before }));
      failed(err, "The logo could not be saved");
    }
  };

  const previewTheme = (id) => { applyTheme(id); setBranding({ ...branding, theme_preset: id }); };

  return (
    <div data-testid="tadmin-branding">
      <div className="flex flex-wrap justify-between items-end gap-3 mb-6">
        <div><div className="admin-overline">White-label</div><h1 className="admin-heading text-3xl font-bold mt-1">Branding & PWA</h1></div>
        <button onClick={save} className="bg-black text-white px-5 py-2.5 text-xs font-semibold uppercase tracking-wider" data-testid="tadmin-branding-save">Save changes</button>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <div className="bg-white border border-neutral-200 p-4 sm:p-6 space-y-5 min-w-0">
          <div className="admin-overline">Identity</div>
          <div>
            <label className="admin-overline">App name</label>
            <input value={branding.app_name} onChange={(e) => setBranding({ ...branding, app_name: e.target.value })} data-testid="tadmin-branding-app-name" className="w-full mt-1 border border-neutral-300 px-3 py-2.5 text-sm focus:outline-none focus:border-black" />
            <div className="text-[11px] text-neutral-500 mt-1">Used as your installable PWA name on customers' home screens.</div>
          </div>
          <div>
            <label className="admin-overline">Tagline</label>
            <input value={branding.tagline || ""} onChange={(e) => setBranding({ ...branding, tagline: e.target.value })} className="w-full mt-1 border border-neutral-300 px-3 py-2.5 text-sm focus:outline-none focus:border-black" />
          </div>

          <ImageUploader
            label="Logo / App icon (square PNG, 1024×1024 recommended)"
            value={branding.logo_url}
            onChange={saveLogo}
            folder="branding/logo"
            shape="square"
            testid="tadmin-branding-logo"
          />
          <div className="text-[11px] text-neutral-500 -mt-3">Saved as soon as it is uploaded. Used as your PWA app icon, browser favicon, and the icon and splash screen of your Android and iOS apps (from their next build).</div>

          <ImageUploader
            label="Hero banner (16:9)"
            value={branding.banner_url}
            onChange={(url) => setBranding({ ...branding, banner_url: url })}
            folder="branding/banner"
            shape="wide"
            testid="tadmin-branding-banner"
          />
        </div>

        <div className="bg-white border border-neutral-200 p-4 sm:p-6 min-w-0">
          <div className="admin-overline mb-4">Live preview</div>
          <div className="rounded-2xl overflow-hidden brand-card p-5 brand-body">
            <div className="flex items-center gap-3 mb-3">
              {branding.logo_url && <img src={branding.logo_url} alt="" className="h-12 w-12 rounded-xl object-cover" />}
              <div>
                <div className="brand-heading text-xl font-bold leading-tight">{branding.app_name || "Your app"}</div>
                <div className="text-xs brand-text-muted">{branding.tagline || "Your tagline"}</div>
              </div>
            </div>
            <button className="mt-2 brand-primary px-5 py-2.5 rounded-full text-sm font-semibold">Book Now</button>
          </div>
          <div className="mt-4 text-xs text-neutral-500 leading-relaxed">
            Your app name, logo and theme are used on your customer website and in your Android and iOS apps.
          </div>
        </div>
      </div>

      <div className="mt-8">
        <div className="admin-overline mb-4">Theme presets — pick one</div>
        <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-3" data-testid="tadmin-theme-picker">
          {THEME_LIST.map((t) => {
            const on = branding.theme_preset === t.id;
            return (
              <button key={t.id} onClick={() => previewTheme(t.id)}
                className={`bg-white border ${on ? "border-black" : "border-neutral-200"} p-5 text-left hover:border-neutral-400 relative`}
                data-testid={`tadmin-theme-${t.id}`}
              >
                {on && <div className="absolute top-3 right-3 bg-black text-white p-1 rounded-full"><Check className="h-3 w-3" /></div>}
                <div className="flex items-center gap-2 mb-3">
                  {t.swatch.map((c, i) => <div key={i} className="h-6 w-6 rounded-full border border-neutral-200" style={{ background: c }} />)}
                </div>
                <div className="admin-heading font-bold text-base">{t.name}</div>
                <div className="text-xs text-neutral-500 mt-1 leading-relaxed">{t.vibe}</div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-neutral-400 mt-3">{t.mode} mode</div>
                <div className="text-[10px] text-neutral-500 mt-1">Fonts: {t.fonts.heading.split(",")[0].replace(/'/g, "")} / {t.fonts.body.split(",")[0].replace(/'/g, "")}</div>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}
